import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import auth from '@/services/http/api/auth'
import formats from '@/constants/momentFormats'
import token from '@/services/token/index'
import type {
  User,
  InactivityModalState,
  LoginCredentials,
  ValidateResponse,
  AuthError,
  MfaVerifyPayload
} from '@/types'
import { isMfaResponse } from '@/types/auth'
import { DEFAULT_AUTH_RETRY_AFTER_SECONDS, AUTH_ERRORS } from '@/constants/auth'
import { HTTP_STATUS } from '@/constants/httpStatus'
import { useClearStoresOnLogout } from '@/composables/useClearStoresOnLogout'

/**
 * Reads the `retry-after` header value in seconds.
 *
 * Falls back to {@link DEFAULT_AUTH_RETRY_AFTER_SECONDS} when the backend omits
 * the header (e.g. on the initial `codeSent` response in some environments) so
 * the resend cooldown / blocked countdown always has a sensible value.
 */
function readRetryAfterSeconds(
  response: { headers?: Record<string, unknown> } | undefined
): number {
  const raw = response?.headers?.['retry-after']
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_AUTH_RETRY_AFTER_SECONDS
}

/**
 * Duck-typed alternative to `axios.isAxiosError` so tests can throw plain
 * `{ response: { status, headers } }` objects without instantiating real
 * `AxiosError`s.
 */
function getResponseStatus(err: unknown): number | undefined {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { status?: number } }).response
    return response?.status
  }
  return undefined
}

function getResponseFromError(err: unknown): { headers?: Record<string, unknown> } | undefined {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    return (err as { response?: { headers?: Record<string, unknown> } }).response
  }
  return undefined
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const loader = ref<boolean | null>(null)
    const user = ref<User | null>(null)
    const forcedToLogout = ref<boolean>(false)
    const isRefreshRequestSent = ref<boolean>(false)
    const inactivityModalVisible = ref<InactivityModalState>({
      modal: false,
      time: 0
    })

    const mfaRequired = ref<boolean>(false)
    const mfaCodeSentTo = ref<string>('')
    const authError = ref<AuthError>(null)
    const mfaResendCooldownSeconds = ref<number>(0)
    const authBlockedRetryAfterSeconds = ref<number>(DEFAULT_AUTH_RETRY_AFTER_SECONDS)
    const mfaVerifyRateLimited = ref<boolean>(false)
    const loginCredentials = ref<LoginCredentials | null>(null)

    const isLogged = computed(() => user.value !== null)
    const authBlocked = computed(() => authError.value === AUTH_ERRORS.blocked)
    const timeToLogout = computed(() => {
      return token
        .dayjs()
        .startOf('hour')
        .second(inactivityModalVisible.value.time)
        .format(formats.MINUTE_WITH_SEC)
    })
    const userHasPermission = computed(
      () => (permission: string) => user.value?.permissions?.includes(permission) ?? false
    )

    const clearFlags = () => {
      inactivityModalVisible.value = {
        modal: false,
        time: 0
      }
      isRefreshRequestSent.value = false
    }

    const finalizeAuthenticatedSession = async (
      validateResponse: Awaited<ReturnType<typeof auth.validate>>
    ): Promise<void> => {
      user.value = validateResponse.data.data
      token.setTokenExpirationTimestamp(
        Number(validateResponse.headers[token.tokenExpirationHeader])
      )
      await tokenRegeneration()
    }

    /**
     * Maps an HTTP error to its {@link AuthError} kind. Falls through to
     * `'unexpected'` so the form-side composable can route the user to the
     * shared `UnexpectedErrorModal`.
     *
     * The MFA `'invalid'` mapping is intentionally NOT included here -- it is
     * specific to verifyMfaCode (a wrong 6-digit code) and would otherwise
     * collide with the LoginForm meaning of 400/401 (wrong credentials).
     */
    const authErrorFromStatus = (status: number | undefined): AuthError => {
      if (status === HTTP_STATUS.BAD_REQUEST || status === HTTP_STATUS.UNAUTHORIZED) {
        return AUTH_ERRORS.unauthorized
      }
      if (status === HTTP_STATUS.FORBIDDEN) {
        return AUTH_ERRORS.forbidden
      }
      return AUTH_ERRORS.unexpected
    }

    /**
     * Authenticates the user, branching into MFA when the backend requests it.
     *
     * Errors are surfaced through {@link authError} so `LoginForm` can render
     * a shared inline alert (400/401 -> `unauthorized`, 403 -> `forbidden`)
     * or open the global `UnexpectedErrorModal` (5xx / network). On `429`
     * we flip {@link authError} to `'blocked'` so the {@link authBlocked}
     * computed lights up and `LoginView` swaps to the standalone
     * `LoginBlocked` view. Cached credentials are kept only when the attempt
     * enters the MFA branch (so {@link resendMfaCode} can re-use them).
     */
    const login = async (payload: LoginCredentials): Promise<ValidateResponse | void> => {
      loader.value = true
      mfaVerifyRateLimited.value = false
      authError.value = null
      loginCredentials.value = payload

      try {
        const authResponse = await auth.authenticate(payload)
        if (isMfaResponse(authResponse.data)) {
          mfaRequired.value = true
          mfaCodeSentTo.value = authResponse.data.email
          mfaResendCooldownSeconds.value = readRetryAfterSeconds(authResponse)
          return
        }

        const validateResponse = await auth.validate(authResponse.data.token)
        await finalizeAuthenticatedSession(validateResponse)
        return validateResponse.data
      } catch (err) {
        const status = getResponseStatus(err)
        if (status === HTTP_STATUS.TOO_MANY_REQUESTS) {
          authError.value = AUTH_ERRORS.blocked
          authBlockedRetryAfterSeconds.value = readRetryAfterSeconds(getResponseFromError(err))
          return
        }
        authError.value = authErrorFromStatus(status)
      } finally {
        loader.value = false
        if (!mfaRequired.value) {
          loginCredentials.value = null
        }
      }
    }

    const clearMfaState = (): void => {
      mfaRequired.value = false
      mfaCodeSentTo.value = ''
      authError.value = null
      mfaResendCooldownSeconds.value = 0
      loginCredentials.value = null
    }

    const clearAuthBlocked = (): void => {
      if (authError.value === AUTH_ERRORS.blocked) {
        authError.value = null
      }
      authBlockedRetryAfterSeconds.value = DEFAULT_AUTH_RETRY_AFTER_SECONDS
    }

    /**
     * Verifies the MFA code and finalizes the session on success.
     *
     * On `429` the backend has discarded the verification code, so we drop the
     * user back to `LoginForm` (via {@link clearMfaState}) with the
     * {@link mfaVerifyRateLimited} alert. Other failures are surfaced through
     * {@link authError}: 400/401 -> `'invalid'` (wrong code, stay on the MFA
     * form), 403 -> `'forbidden'` (system not yet open -- alert rendered by
     * the form), and any remaining 4xx/5xx/network error -> `'unexpected'`
     * which the form composable routes to the shared `UnexpectedErrorModal`.
     */
    const verifyMfaCode = async (payload: MfaVerifyPayload): Promise<void> => {
      loader.value = true
      authError.value = null
      try {
        const authResponse = await auth.verifyMfaCode(payload)
        const validateResponse = await auth.validate(authResponse.data.token)
        await finalizeAuthenticatedSession(validateResponse)
        clearMfaState()
      } catch (err) {
        const status = getResponseStatus(err)
        if (status === HTTP_STATUS.BAD_REQUEST || status === HTTP_STATUS.UNAUTHORIZED) {
          authError.value = AUTH_ERRORS.invalid
          return
        }
        if (status === HTTP_STATUS.FORBIDDEN) {
          authError.value = AUTH_ERRORS.forbidden
          return
        }
        if (status === HTTP_STATUS.TOO_MANY_REQUESTS) {
          clearMfaState()
          mfaVerifyRateLimited.value = true
          return
        }
        authError.value = AUTH_ERRORS.unexpected
      } finally {
        loader.value = false
      }
    }

    /**
     * Re-requests an MFA code for the cached login credentials.
     *
     * The cooldown is reset to `0` BEFORE the request so a returning identical
     * `retry-after` value still produces a `0 -> N` transition that the
     * `MfaVerificationForm` watcher can observe (Vue does not consider equal
     * writes a change). It also leaves the cooldown at `0` on failure so the
     * user can retry immediately.
     *
     * On `429` we route the user to `LoginBlocked` via
     * {@link authError}=`'blocked'`; the `retry-after` value must be captured
     * before {@link clearMfaState} resets surrounding state. Other failures
     * surface through {@link authError} (same kinds as
     * {@link verifyMfaCode}).
     */
    const resendMfaCode = async (): Promise<void> => {
      const credentials = loginCredentials.value
      if (!credentials) return

      mfaResendCooldownSeconds.value = 0

      try {
        const response = await auth.authenticate(credentials)
        if (isMfaResponse(response.data)) {
          mfaCodeSentTo.value = response.data.email
        }
        authError.value = null
        mfaResendCooldownSeconds.value = readRetryAfterSeconds(response)
      } catch (err) {
        const status = getResponseStatus(err)
        if (status === HTTP_STATUS.TOO_MANY_REQUESTS) {
          const blockedSeconds = readRetryAfterSeconds(getResponseFromError(err))
          clearMfaState()
          authError.value = AUTH_ERRORS.blocked
          authBlockedRetryAfterSeconds.value = blockedSeconds
          return
        }
        if (status === HTTP_STATUS.FORBIDDEN) {
          authError.value = AUTH_ERRORS.forbidden
          return
        }
        authError.value = AUTH_ERRORS.unexpected
      }
    }

    const logout = async (): Promise<void> => {
      if (loader.value !== true) {
        loader.value = true

        try {
          await auth.logout()
        } finally {
          user.value = null
          clearFlags()
          token.removeAll()
          loginCredentials.value = null
          loader.value = false

          const { clearOnLogout } = useClearStoresOnLogout()
          clearOnLogout()
        }
      }
    }

    const refreshToken = async (): Promise<void> => {
      if (isRefreshRequestSent.value === false) {
        isRefreshRequestSent.value = true
        try {
          const response = await auth.refresh()
          token.setTokenExpirationTimestamp(Number(response.headers[token.tokenExpirationHeader]))
        } finally {
          isRefreshRequestSent.value = false
        }
      }
    }

    const tokenRegeneration = async (): Promise<void> => {
      token.setLastActivityTimestamp(token.dayjs().unix())
      token.listeners()
      token.startRefreshInterval(async () => {
        await refreshToken()
      })
      token.startTokenGuardInterval(
        async () => {
          forcedToLogout.value = true
          await logout()
        },
        (flag: boolean, time: number) => {
          if (flag === false && inactivityModalVisible.value.modal === false) return
          inactivityModalVisible.value = {
            modal: flag,
            time
          }
        }
      )
    }

    const clearUserOnInvalidToken = (): void => {
      const isTokenValid = token.isTokenValid()
      if (!isTokenValid) {
        user.value = null
        clearFlags()
        token.removeAll()
      }
    }

    return {
      loader,
      user,
      forcedToLogout,
      isRefreshRequestSent,
      inactivityModalVisible,
      mfaRequired,
      mfaCodeSentTo,
      authError,
      mfaResendCooldownSeconds,
      authBlockedRetryAfterSeconds,
      mfaVerifyRateLimited,
      loginCredentials,

      isLogged,
      authBlocked,
      timeToLogout,
      userHasPermission,

      clearFlags,
      login,
      logout,
      refreshToken,
      tokenRegeneration,
      clearUserOnInvalidToken,
      clearMfaState,
      clearAuthBlocked,
      verifyMfaCode,
      resendMfaCode
    }
  },
  {
    persist: {
      key: 'auth',
      storage: localStorage,
      pick: ['user']
    }
  }
)
